import { useState } from 'react'
import { useQuery, useMutation } from 'convex/react'
import { useTheme } from '../stores/ThemeContext'
import { useI18n } from '../stores/I18nContext'
import { useAI } from '../stores/AIContext'
import { api } from '../../../convex/_generated/api'
import { forceRefreshApp, getAppVersion, getBuildTime } from '../utils/appRefresh'
import './UserSettingsModal.css'

export default function UserSettingsModal({ isOpen, onClose }) {
  const { theme, setTheme, themes, colorMode, toggleColorMode } = useTheme()
  const { language, setLanguage, languages } = useI18n()
  const { settings: aiSettings } = useAI()
  const updateSettingsMutation = useMutation(api.ai.updateSettings)
  const usageSummary = useQuery(api.usage.getUserUsageSummary)
  const isNorwegian = language === 'nb'

  const [activeTab, setActiveTab] = useState('appearance')
  const [isRefreshing, setIsRefreshing] = useState(false)

  if (!isOpen) return null

  const tabs = [
    { id: 'appearance', label: isNorwegian ? 'Utseende' : 'Appearance' },
    { id: 'ai', label: 'AI' },
    { id: 'app', label: 'App' }
  ]

  const handleRefreshApp = async () => {
    setIsRefreshing(true)
    // Clears caches + activates any waiting service worker, then reloads.
    await forceRefreshApp()
  }

  const buildTime = getBuildTime()
  const buildTimeLabel = buildTime
    ? new Date(buildTime).toLocaleString(isNorwegian ? 'nb-NO' : 'en-US')
    : ''

  const handleProviderChange = async (provider) => {
    await updateSettingsMutation({ aiProvider: provider })
  }

  const handleAvatarModeChange = async (mode) => {
    if (mode === 'ghibli') return // disabled — coming soon
    await updateSettingsMutation({ avatarMode: mode })
  }

  const avatarMode = aiSettings?.avatarMode || 'initials'

  const formatTokens = (count) => {
    if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`
    if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`
    return count.toString()
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="user-settings-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isNorwegian ? 'Brukerinnstillinger' : 'User Settings'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="settings-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="modal-body">
          {activeTab === 'appearance' && (
            <div className="settings-section">
              <div className="form-group">
                <label>{isNorwegian ? 'Tema' : 'Theme'}</label>
                <div className="theme-options">
                  <button
                    className={`theme-option ${theme === themes.ghibli ? 'active' : ''}`}
                    onClick={() => setTheme(themes.ghibli)}
                  >
                    <div className="theme-preview ghibli">
                      <div className="preview-bar" />
                      <div className="preview-content" />
                    </div>
                    <span>Studio Ghibli</span>
                  </button>
                  <button
                    className={`theme-option ${theme === themes.corporate ? 'active' : ''}`}
                    onClick={() => setTheme(themes.corporate)}
                  >
                    <div className="theme-preview corporate">
                      <div className="preview-bar" />
                      <div className="preview-content" />
                    </div>
                    <span>Corporate</span>
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>{isNorwegian ? 'Fargemodus' : 'Color Mode'}</label>
                <div className="color-mode-toggle" style={{ padding: '8px 0' }}>
                  <span className="toggle-label">
                    {colorMode === 'light'
                      ? (isNorwegian ? '☀️ Lys modus' : '☀️ Light mode')
                      : (isNorwegian ? '🌙 Mørk modus' : '🌙 Dark mode')}
                  </span>
                  <button
                    className={`toggle-switch ${colorMode === 'light' ? 'active' : ''}`}
                    onClick={toggleColorMode}
                    aria-label="Toggle light/dark mode"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{isNorwegian ? 'Språk' : 'Language'}</label>
                <div className="language-options">
                  {Object.entries(languages).map(([code, name]) => (
                    <button
                      key={code}
                      className={`language-option ${language === code ? 'active' : ''}`}
                      onClick={() => setLanguage(code)}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>{isNorwegian ? 'Avatar' : 'Avatar'}</label>
                <div className="avatar-mode-options">
                  <button
                    type="button"
                    className={`avatar-mode-option ${avatarMode === 'initials' ? 'active' : ''}`}
                    onClick={() => handleAvatarModeChange('initials')}
                  >
                    <span className="avatar-mode-name">{isNorwegian ? 'Initialer' : 'Initials'}</span>
                    <span className="avatar-mode-hint">{isNorwegian ? 'Standard' : 'Default'}</span>
                  </button>
                  <button
                    type="button"
                    className={`avatar-mode-option ${avatarMode === 'image' ? 'active' : ''}`}
                    onClick={() => handleAvatarModeChange('image')}
                  >
                    <span className="avatar-mode-name">{isNorwegian ? 'Profilbilde' : 'Profile image'}</span>
                    <span className="avatar-mode-hint">{isNorwegian ? 'Fra kontoen din' : 'From your account'}</span>
                  </button>
                  <button
                    type="button"
                    className={`avatar-mode-option ${avatarMode === 'ghibli' ? 'active' : ''}`}
                    onClick={() => handleAvatarModeChange('ghibli')}
                    disabled
                    title={isNorwegian ? 'Kommer snart' : 'Coming soon'}
                  >
                    <span className="avatar-mode-name">{isNorwegian ? 'Ghibli-avatar' : 'Ghibli avatar'}</span>
                    <span className="avatar-mode-hint">{isNorwegian ? 'Kommer snart' : 'Coming soon'}</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'ai' && (
            <div className="settings-section">
              <div className="form-group">
                <label>{isNorwegian ? 'AI-leverandør' : 'AI Provider'}</label>
                <div className="provider-options">
                  <button
                    className={`provider-option ${(aiSettings?.aiProvider || 'openai') === 'anthropic' ? 'active' : ''}`}
                    onClick={() => handleProviderChange('anthropic')}
                  >
                    <span className="provider-name">Claude</span>
                    <span className="provider-model">Sonnet</span>
                  </button>
                  <button
                    className={`provider-option ${(aiSettings?.aiProvider || 'openai') === 'openai' ? 'active' : ''}`}
                    onClick={() => handleProviderChange('openai')}
                  >
                    <span className="provider-name">OpenAI</span>
                    <span className="provider-model">GPT-4o</span>
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>{isNorwegian ? 'Bruk (siste 30 dager)' : 'Usage (last 30 days)'}</label>
                {usageSummary ? (
                  <div className="usage-stats">
                    <div className="usage-stat">
                      <span className="usage-stat-value">{usageSummary.totalRequests}</span>
                      <span className="usage-stat-label">{isNorwegian ? 'Forespørsler' : 'Requests'}</span>
                    </div>
                    <div className="usage-stat">
                      <span className="usage-stat-value">{formatTokens(usageSummary.totalTokens)}</span>
                      <span className="usage-stat-label">Tokens</span>
                    </div>
                    <div className="usage-stat">
                      <span className="usage-stat-value">${usageSummary.totalCostUSD.toFixed(4)}</span>
                      <span className="usage-stat-label">{isNorwegian ? 'Est. kostnad' : 'Est. cost'}</span>
                    </div>

                    {Object.entries(usageSummary.byProvider).length > 0 && (
                      <div className="usage-breakdown">
                        <label>{isNorwegian ? 'Per leverandør' : 'By provider'}</label>
                        {Object.entries(usageSummary.byProvider).map(([provider, data]) => (
                          <div key={provider} className="usage-breakdown-row">
                            <span className="breakdown-provider">
                              {provider === 'anthropic' ? 'Claude' : 'OpenAI'}
                            </span>
                            <span className="breakdown-detail">
                              {data.requests} req · {formatTokens(data.tokens)} tokens · ${data.costUSD.toFixed(4)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="usage-empty">
                    {isNorwegian ? 'Ingen bruksdata ennå.' : 'No usage data yet.'}
                  </p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'app' && (
            <div className="settings-section">
              <div className="form-group">
                <label>{isNorwegian ? 'Oppdater appen' : 'Refresh app'}</label>
                <p className="settings-hint">
                  {isNorwegian
                    ? 'Tvinger en fullstendig ny innlasting og tømmer hurtigbufferen. Bruk dette etter en ny utgivelse hvis appen oppfører seg gammelt eller mangler nye funksjoner.'
                    : 'Forces a full reload and clears cached files. Use this after a new release if the app behaves like an old version or is missing new functions.'}
                </p>
                <button
                  type="button"
                  className="btn btn-primary refresh-app-btn"
                  onClick={handleRefreshApp}
                  disabled={isRefreshing}
                >
                  {isRefreshing
                    ? (isNorwegian ? 'Oppdaterer…' : 'Refreshing…')
                    : (isNorwegian ? '🔄 Oppdater appen' : '🔄 Refresh app')}
                </button>
              </div>

              <div className="form-group">
                <label>{isNorwegian ? 'Versjon' : 'Version'}</label>
                <div className="app-version-info">
                  <span className="app-version">v{getAppVersion()}</span>
                  {buildTimeLabel && (
                    <span className="app-build-time">
                      {isNorwegian ? 'Bygget' : 'Built'}: {buildTimeLabel}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>
            {isNorwegian ? 'Lukk' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  )
}
